import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Anonymous device identity: lets users save to our DB without logging in.
// A stable UUID is stored in localStorage and sent as X-Device-Id on every request.
export const getDeviceId = () => {
  let id = localStorage.getItem('device_id');
  if (!id) {
    id = (crypto?.randomUUID?.() || `dev-${Date.now()}-${Math.random().toString(36).slice(2)}`);
    localStorage.setItem('device_id', id);
  }
  return id;
};

// Build the auth query param for direct browser downloads (which can't set headers).
// Uses the JWT token when signed in, otherwise the anonymous device id.
export const getDownloadAuthParam = () => {
  const token = localStorage.getItem('token');
  if (token) return `token=${encodeURIComponent(token)}`;
  return `device_id=${encodeURIComponent(getDeviceId())}`;
};

const api = axios.create({ baseURL: API, headers: { 'Content-Type': 'application/json' } });

// Request interceptor: always attach device id, plus token when signed in
api.interceptors.request.use(
  (config) => {
    config.headers['X-Device-Id'] = getDeviceId();
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor: on 401 for a signed-in session, drop the stale token so the
// app falls back to anonymous device mode instead of forcing a login gateway.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const isAuthEndpoint = error.config?.url?.includes('/auth/');
    if (error.response?.status === 401 && !isAuthEndpoint) {
      localStorage.removeItem('token');
      delete api.defaults.headers.common['Authorization'];
    }
    return Promise.reject(error);
  }
);

export const authApi = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  me: () => api.get('/auth/me'),
};

export const projectsApi = {
  getAll: () => api.get('/projects'),
  getById: (id) => api.get(`/projects/${id}`),
  create: (data) => api.post('/projects', data),
  update: (id, data) => api.put(`/projects/${id}`, data),
  delete: (id) => api.delete(`/projects/${id}`),
  clone: (id) => api.post(`/projects/${id}/clone`),
  getEstimate: (id) => api.get(`/projects/${id}/estimate`),
};

export const roomsApi = {
  add: (projectId, data) => api.post(`/projects/${projectId}/rooms`, data),
  update: (projectId, roomId, data) => api.put(`/projects/${projectId}/rooms/${roomId}`, data),
  delete: (projectId, roomId) => api.delete(`/projects/${projectId}/rooms/${roomId}`),
  clone: (projectId, roomId) => api.post(`/projects/${projectId}/rooms/${roomId}/clone`),
};

export const exteriorApi = {
  add: (projectId, data) => api.post(`/projects/${projectId}/exterior`, data),
  update: (projectId, sideId, data) => api.put(`/projects/${projectId}/exterior/${sideId}`, data),
  delete: (projectId, sideId) => api.delete(`/projects/${projectId}/exterior/${sideId}`),
};

export const deckApi = {
  add: (projectId, data) => api.post(`/projects/${projectId}/deck`, data),
  update: (projectId, deckId, data) => api.put(`/projects/${projectId}/deck/${deckId}`, data),
  delete: (projectId, deckId) => api.delete(`/projects/${projectId}/deck/${deckId}`),
};

export const cabinetsApi = {
  add: (projectId, data) => api.post(`/projects/${projectId}/cabinet`, data),
  update: (projectId, cabinetId, data) => api.put(`/projects/${projectId}/cabinet/${cabinetId}`, data),
  delete: (projectId, cabinetId) => api.delete(`/projects/${projectId}/cabinet/${cabinetId}`),
};

export const settingsApi = {
  get: () => api.get('/settings'),
  update: (data) => api.put('/settings', data),
};

export const ratesApi = {
  get: () => api.get('/production-rates'),
  updateRate: (category, itemKey, rate) => api.put(`/production-rates/${category}/${itemKey}`, null, { params: { rate } }),
  resetRate: (category, itemKey) => api.delete(`/production-rates/${category}/${itemKey}`),
};

export const documentsApi = {
  proposalUrl: (projectId, format = 'pdf') => `${API}/projects/${projectId}/documents/proposal?format=${format}&${getDownloadAuthParam()}`,
  agreementUrl: (projectId, format = 'pdf') => `${API}/projects/${projectId}/documents/agreement?format=${format}&${getDownloadAuthParam()}`,
};

export const adminApi = {
  getUsers: (search = '') => api.get('/admin/users', { params: { search } }),
  resetPassword: (userId) => api.put(`/admin/users/${userId}/reset-password`),
  updateRole: (userId, role) => api.put(`/admin/users/${userId}/role`, null, { params: { role } }),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
};

export default api;
